import React, { useEffect, useState } from "react";
import Plot from "react-plotly.js";
import { Modal, Button, Skeleton, theme } from "antd";
import { BarChartOutlined, CaretDownOutlined, CaretUpOutlined } from "@ant-design/icons";
import AutoSizer from "react-virtualized-auto-sizer";
import TooltipContainer from "../components/UI/TooltipContainer";
import { unparse } from "papaparse";
import { useDebounce } from "@uidotdev/usehooks";
import { useSelector } from "react-redux";

function PlotContainer(props) {

  const icon = {
    width: 1000,
    path: "m938 413l-188-125c0 37-17 71-44 94 64 38 107 107 107 187 0 121-98 219-219 219-121 0-219-98-219-219 0-61 25-117 66-156h-115c30 33 49 76 49 125 0 103-84 187-187 187s-188-84-188-187c0-57 26-107 65-141-38-22-65-62-65-109v-250c0-70 56-126 125-126h500c69 0 125 56 125 126l188-126c34 0 62 28 62 63v375c0 35-28 63-62 63z m-750 0c-69 0-125 56-125 125s56 125 125 125 125-56 125-125-56-125-125-125z m406-1c-87 0-157 70-157 157 0 86 70 156 157 156s156-70 156-156-70-157-156-157z",
    ascent: 1000,
    descent: -350,
  };

  const icon2 = {
    width: 1000,
    path: "m750 375a168 168 0 0 0-156.8-167.3 140 140 0 0 0-280.4 131.8h105a14 14 0 0 1 10.08 4.2l76.72 78.12a14 14 0 0 0 21.02 0l76.72-78.12a14 14 0 0 1 10.08-4.2h105a112 112 0 0 0 2.48-225.6zm-238 364v-206.54l-32.2 32.2a14 14 0 1 1-19.6-19.88l56-56a14 14 0 0 1 19.6 0l56 56a14 14 0 0 1-19.6 19.88l-32.2-32.2v206.54a14 14 0 0 1-28 0z",
    ascent: 1000,
    descent: -350,
  };

  const { token } = theme.useToken();
  const [useApiArgs, setUseApiArgs] = useState();
  const [data, setData] = useState([]);
  const [layout, setLayout] = useState({});
  const [error, setError] = useState("");
  const [downloadData, setDownloadData] = useState([]);
  const [showCharts, setShowCharts] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [plotHeight, setPlotHeight] = useState(0);
  const [plotWidth, setPlotWidth] = useState(0);
  const debouncedPlotHeight = useDebounce(plotHeight, 500);
  const debouncedPlotWidth = useDebounce(plotWidth, 500);
  const debouncedUseApiArgs = useDebounce(props.useApiArgs, 100);
  const debouncedError = useDebounce(error, 3000);
  const api = props.useApi(useApiArgs);
  const isPopup = props.isPopup ? props.isPopup : true;
  const tooltipTitle = props.tooltipTitle ? props.tooltipTitle : "WIP";
  const [resizeKey, setResizeKey] = useState(0);
  const [isResizing, setIsResizing] = useState(false);
  const collapsed = useSelector((state) => state.sider.collapsed);
  const [extraButtons, setExtraButtons] = useState([
    {
      name: "Fullscreen",
      click: () => handleOpenModal(),
      icon: icon,
    },
  ]);
  const isSparkline = props.isSparkline ? props.isSparkline : false;

  const hexToRgb = (hex) => {
    if (!hex) return "0, 0, 0";
    const cleaned = hex.replace(/^#/, "");
    const r = parseInt(cleaned.substring(0, 2), 16);
    const g = parseInt(cleaned.substring(2, 4), 16);
    const b = parseInt(cleaned.substring(4, 6), 16);
    return `${isNaN(r) ? 0 : r}, ${isNaN(g) ? 0 : g}, ${isNaN(b) ? 0 : b}`;
  };

  const popValueRaw = isSparkline && api && api.data ? parseFloat(api.data.pop) : null;
  const isPopValueValid =
    isSparkline && popValueRaw !== null && !Number.isNaN(popValueRaw) && popValueRaw !== 0;
  const isPopPositive = isPopValueValid ? popValueRaw > 0 : null;
  const isInverted = api?.data?.is_inverted;
  const root = typeof window !== "undefined" ? getComputedStyle(document.documentElement) : null;

  let sparkPopBgColor = null;
  let sparkPopTextColor = null;
  if (root && isPopValueValid) {
    let baseHex = null;
    if (isInverted) {
      baseHex = isPopPositive
        ? root.getPropertyValue("--red-color").trim()
        : root.getPropertyValue("--green-color").trim();
    } else {
      baseHex = isPopPositive
        ? root.getPropertyValue("--green-color").trim()
        : root.getPropertyValue("--red-color").trim();
    }
    const rgb = hexToRgb(baseHex);
    sparkPopBgColor = `rgba(${rgb}, 0.5)`;
    sparkPopTextColor = "var(--primary-color)";
  }

  useEffect(() => {
    // Delay the resize trigger slightly to allow the sidebar animation to complete
    setIsResizing(true);
    const timer = setTimeout(() => {
      setResizeKey((prev) => prev + 1);
      setIsResizing(false);
    }, 200);

    return () => clearTimeout(timer);
  }, [collapsed]);

  useEffect(() => {
    if (api.status === "fulfilled") {
      const parsedData = JSON.parse(api.data.plot);
      setData(parsedData.data);
      setLayout(parsedData.layout);
      if (parsedData.downloadData) {
        console.log("Downloadable data: ", parsedData.downloadData);
        setDownloadData(parsedData.downloadData);
        console.log("Downloadable data: ", downloadData);
      }
      setShowCharts(false);
      setError("");
    } else if (api.status === "rejected") {
      setData([]);
      setLayout({});
      setShowCharts(true);
      setError("Error");
    } else if (api.status === "pending") {
      setData([]);
      setLayout({});
      setShowCharts(false);
      setError("");
    } else {
      setData([]);
      setLayout({});
      setShowCharts(false);
      setError("Error");
    }
  }, [api]);

  useEffect(() => {
    console.log("width", debouncedPlotWidth);
    console.log("height", debouncedPlotHeight);
    console.log("api args", debouncedUseApiArgs);

    var tmpApiArgs = { ...debouncedUseApiArgs };

    var fontSize = 12;
    var titleFontSize = 16;
    // calculate font size
    if (debouncedPlotHeight > 0 && debouncedPlotWidth > 0) {
      fontSize = (debouncedPlotHeight + debouncedPlotWidth * 3) / 275;
      // ceiling int
      fontSize = fontSize > 16 ? 12 : Math.ceil(fontSize);
    }

    // calculate title font size
    if (debouncedPlotHeight > 0 && debouncedPlotWidth > 0) {
      titleFontSize = (debouncedPlotHeight + debouncedPlotWidth * 3) / 175;
      // ceiling int
      titleFontSize = titleFontSize > 16 ? 16 : Math.ceil(titleFontSize);
    }

    console.log("api args");
    // if data is in the api args, add to it
    if (tmpApiArgs.data) {
      tmpApiArgs.data = {
        ...tmpApiArgs.data,
        font_size: fontSize,
        title_font_size: titleFontSize,
      };
    }

    setUseApiArgs(tmpApiArgs);
  }, [debouncedUseApiArgs, debouncedPlotHeight, debouncedPlotWidth]);

  const handleOpenModal = () => {
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
  };

  useEffect(() => {
    const downloadRawDataButton = {
      name: "Download Raw Data",
      click: () => {
        const csv = unparse(downloadData); // Converts array of objects to CSV
        const blob = new Blob([csv], {
          type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "data.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      },
      icon: icon2,
    };

    if (downloadData.length > 0) {
      setExtraButtons([
        {
          name: "Fullscreen",
          click: () => handleOpenModal(),
          icon: icon,
        },
        downloadRawDataButton,
      ]);
    } else {
      setExtraButtons([
        {
          name: "Fullscreen",
          click: () => handleOpenModal(),
          icon: icon,
        },
      ]);
    }
  }, [downloadData]);

  return (
    <>
      {/* <> */}
      <AutoSizer
        style={{
          borderRadius: token.cardRadius,
          height: "100%",
          minHeight: isSparkline ? "148px" : "40.2vh",
          minWidth: "100%",
          width: "100%",
          margin: "0",
          padding: "0",
          backgroundColor: "var(--tertiary-color)",
        }}
        className="soft-shadow"
        onResize={({ height, width }) => {
          setPlotHeight(height);
          setPlotWidth(width);
        }}
        key={resizeKey}
      >
        {({ height, width }) => (
          <div
            style={{
              width: width,
              height: height,
            }}
          >
            <div
              style={{
                display: "flex",
                margin: "0",
                width: width,
                height: height,
                flexDirection: "column",
                animation: "fadeIn 1.5s",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {tooltipTitle === "WIP" ? null : (
                <TooltipContainer
                  title={tooltipTitle}
                  style={{
                    alignSelf: "flex-end",
                    margin: "0",
                    marginRight: "0.3vw",
                  }}
                />
              )}
              {api.isFetching ? (
                <div
                  style={{
                    width: width,
                    height: height,
                  }}
                >
                  <Skeleton.Node
                    active
                    style={{
                      width: width,
                      height: height,
                      borderRadius: token.cardRadius,
                    }}
                  >
                    <BarChartOutlined
                      style={{
                        fontSize: "3em",
                        color: "var(--green-color)",
                      }}
                    />
                  </Skeleton.Node>
                </div>
              ) : (
                <>
                  <h2
                    style={{
                      color: "red",
                      textAlign: "center",
                      display: debouncedError === "" ? "none" : "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    {debouncedError}
                  </h2>
                  {isSparkline && api.data && api.data.title && <p
                    style={{
                      fontSize: "1.1em",
                      fontWeight: "600",
                      fontFamily: "var(--co-font-primary)",
                      // maxWidth: "10vw",
                      marginBottom: "0",
                      width: "100%",
                      textAlign: "center",
                      marginTop: "1.5em",
                      // backgroundColor: "red",
                    }}
                  >
                {api.data.title}
                </p>}
                  {isSparkline && isPopValueValid && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "24px",
                        width: "60%",
                        justifyContent: "center",
                        marginTop: "0.5em",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: sparkPopBgColor,
                          borderRadius: "6px",
                          padding: "0.3rem 0.4rem",
                          fontSize: "0.82em",
                          fontWeight: "600",
                          minWidth: "4vw",
                          width: "100%",
                          maxHeight: "24px",
                          textAlign: "center",
                          alignContent: "center",
                          color: sparkPopTextColor,
                          gap: "8px",
                        }}
                      >
                        {isPopPositive ? <CaretUpOutlined /> : <CaretDownOutlined />}
                        {popValueRaw}%
                      </div>
                    </div>
                  )}
                  {isSparkline && api.data && api.data.title && <p
                    style={{
                      fontSize: api.data.sum === "No Data Available" ? "1.4em" : "2.2em",
                      width: "100%",
                      color: api.data.sum === "No Data Available" ? "var(--medium-light-grey-color)" : "inherit",
                      fontWeight: "bold",
                      fontFamily: "var(--co-font-primary)",
                      alignContent: "center",
                      marginBottom: "0px",
                      marginTop: "0.5em",
                      textAlign: "center",
                    }}
                  >
              {api.data.sum}
            </p>}
                  <Plot
                    data={data}
                    layout={layout}
                    style={{
                      width: width - 52,
                      height: isSparkline ? (height - 15) / 2 : height - 15,
                      display: debouncedError === "" ? "" : "none",
                    }}
                    config={{
                      responsive: true,
                      displaylogo: false,
                      modeBarButtonsToAdd: isSparkline ? [] : extraButtons,
                      toImageButtonOptions: {
                        format: "png",
                        filename: "plot",
                        height: 900,
                        width: 1600,
                        scale: 1,
                      },
                      plotlyServerURL: "https://chart-studio.plotly.com",
                      displayModeBar: isSparkline ? false : "hover",
                      showEditInChartStudio: !isSparkline,
                    }}
                  />
                </>
              )}
            </div>
          </div>
        )}
      </AutoSizer>
      {/* </> */}
      {isPopup && (
        <Modal
          open={isModalVisible}
          onCancel={handleCloseModal}
          footer={null}
          centered // Ensures the modal is centered
          styles={{
            body: {
              padding: 0,
              backgroundColor: "var(--tertiary-color)", // Use the correct background color
              width: "88vw",
              height: "88vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            },
            content: {
              padding: 0,
              backgroundColor: "var(--tertiary-color", // Use the correct background color
              width: "92vw",
              height: "92vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              // make the corners round
              borderRadius: "20px",
            },
          }}
          width="90vw" // Adjust width of the modFal
          height="90vh" // Adjust height of the modal
        >
          <Plot
            data={data}
            layout={{
              ...layout,
            }}
            style={{ width: "100%", height: "100%", animation: "fadeIn 1.5s" }}
            config={{
              responsive: true,
              displaylogo: false,
              plotlyServerURL: "https://chart-studio.plotly.com",
              displayModeBar: "hover",
              showEditInChartStudio: true,
            }}
          />
        </Modal>
      )}
    </>
  );
}

export default PlotContainer;
